import Color from 'color';
import { isTruthy, isDefined } from '@fabric-app/utils/guards';
import { isResourceDetailImage, isResourceDetailBookmark, isResourceDetailHighlight, isResourceDetailDocument, isResourceDetailNotepad, isResourceDetailVideo, isResourceDetailAudio } from '../resources/utils/resourceDetailGuards.js';
import { formatter, attributeDateFormatter } from './components/ExpandedResource/ExtraPanel/utils.js';
import { boolToYesOrNo } from '@fabric-app/utils/text';
import { formatCoordinates, formatDuration, parseIntOrString, formatBitrate, formatSampleRate } from '@fabric-app/utils/number';

const detailsToAttributeMaps = (detail) => {
  if (isResourceDetailImage(detail)) {
    return [
      {
        label: "Dimensions",
        value: formatter(detail.data.attributes?.dimensions, (dim) => `${dim.width}×${dim.height}`)
      },
      {
        label: "Color Space",
        value: detail.data.attributes?.color?.space
      },
      {
        label: "Color Profile",
        value: detail.data.attributes?.color?.profile
      },
      {
        label: "Alpha Channel",
        value: formatter(detail.data.attributes?.color?.alphaChannel, boolToYesOrNo)
      },
      {
        label: "Device",
        value: formatter(detail.data.attributes?.device, (dev) => Object.values(dev).filter(isTruthy).join(", "))
      },
      {
        label: "Location",
        value: formatter(detail.data.attributes?.location, (loc) => isDefined(loc.latitude) && isDefined(loc.longitude) ? formatCoordinates({
          latitude: loc.latitude,
          longitude: loc.longitude
        }) : void 0)
      },
      {
        label: "Main colors",
        value: detail.data.dominantColors && detail.data.dominantColors.length > 0 ? detail.data.dominantColors.map((color) => {
          const hex = Color(color.rgb).hex();
          return {
            label: hex.slice(1),
            color: hex,
            prefix: "#"
          };
        }) : void 0,
        divider: true
      },
      {
        label: "AI summary",
        value: detail.data.caption,
        divider: true
      },
      {
        label: "AI keywords",
        value: detail.data.keywords && detail.data.keywords.length > 0 ? detail.data.keywords.map((keyword) => ({
          label: keyword,
          color: "default"
        })) : void 0,
        divider: true
      }
    ];
  }
  if (isResourceDetailBookmark(detail) || isResourceDetailHighlight(detail)) return [
    {
      label: "Page title",
      value: detail.data?.webpage?.title
    },
    {
      label: "Source URL",
      value: detail.data?.webpage?.url
    },
    {
      label: "AI summary",
      value: detail.data.caption,
      divider: true
    },
    {
      label: "AI keywords",
      value: detail.data.keywords ? detail.data.keywords.map((keyword) => ({
        label: keyword,
        color: "default"
      })) : void 0,
      divider: true
    }
  ];
  if (isResourceDetailDocument(detail)) return [
    {
      label: "Title",
      value: detail.data.attributes?.document?.title
    },
    {
      label: "Subject",
      value: detail.data.attributes?.document?.subject
    },
    {
      label: "Authors",
      value: detail.data.attributes?.document?.author
    },
    {
      label: "Encoding Software",
      value: detail.data.attributes?.document?.producer
    },
    {
      label: "Version",
      value: detail.data.attributes?.document?.version
    },
    {
      label: "Pages",
      value: detail.data.attributes?.document?.pages
    },
    {
      label: "Resolution",
      value: formatter(detail.data.attributes?.dimensions, (dim) => isDefined(dim?.width) && isDefined(dim?.height) ? `${dim?.width}×${dim?.height}` : void 0)
    },
    {
      label: "Security",
      value: formatter(detail.data.attributes?.document?.security, boolToYesOrNo)
    },
    {
      label: "Content creator",
      value: detail.data.attributes?.document?.creator
    },
    {
      label: "Keywords",
      value: formatter(detail.data.attributes?.document?.keywords, (v) => v ? v : void 0)
    },
    {
      label: "Creation Date",
      value: formatter(detail.data.attributes?.document?.creationDate, attributeDateFormatter)
    },
    {
      label: "Modification Date",
      value: formatter(detail.data.attributes?.document?.modificationDate, attributeDateFormatter)
    },
    {
      label: "AI summary",
      value: detail.data.caption,
      divider: true
    },
    {
      label: "AI keywords",
      value: detail.data.keywords && detail.data.keywords.length > 0 ? detail.data.keywords.map((keyword) => ({
        label: keyword,
        color: "default"
      })) : void 0,
      divider: true
    }
  ];
  if (isResourceDetailNotepad(detail)) return [
    {
      label: "Author",
      value: detail.user?.name || "Unknown"
    },
    {
      label: "AI Summary",
      value: detail.data.caption,
      divider: true
    },
    {
      label: "AI keywords",
      value: detail.data.keywords && detail.data.keywords.length > 0 ? detail.data.keywords.map((keyword) => ({
        label: keyword,
        color: "default"
      })) : void 0,
      divider: true
    }
  ];
  if (isResourceDetailVideo(detail)) return [
    {
      label: "Dimensions",
      value: formatter(detail.data.attributes?.dimensions, (dim) => isDefined(dim?.width) && isDefined(dim?.height) ? `${dim?.width}×${dim?.height}` : void 0)
    },
    {
      label: "Codec",
      value: detail.data.attributes?.video?.codec
    },
    {
      label: "Color Space",
      value: detail.data.attributes?.color?.space
    },
    {
      label: "Color Profile",
      value: detail.data.attributes?.color?.profile
    },
    {
      label: "Duration",
      value: formatter(detail.data.attributes?.video?.duration, formatDuration)
    },
    {
      label: "Bit Rate",
      value: formatter(detail.data.attributes?.video?.bitRate, (bitRate) => `${parseIntOrString(bitRate).toLocaleString("en-US", {
        style: "decimal",
        maximumFractionDigits: 0,
        useGrouping: true
      })} bps (${formatBitrate(bitRate)})`)
    },
    {
      label: "Frame Rate",
      value: formatter(detail.data.attributes?.video?.frameRate, (fps) => `${fps} fps`)
    },
    {
      label: "Audio channels",
      value: detail.data.attributes?.audio?.channels
    },
    {
      label: "Alpha Channel",
      value: formatter(detail.data.attributes?.color?.alphaChannel, boolToYesOrNo)
    },
    {
      label: "Device",
      value: formatter(detail.data.attributes?.device, (dev) => Object.values(dev).filter(isTruthy).join(", "))
    },
    {
      label: "Location",
      value: formatter(detail.data.attributes?.location, (loc) => isDefined(loc.latitude) && isDefined(loc.longitude) ? formatCoordinates({
        latitude: loc.latitude,
        longitude: loc.longitude
      }) : void 0)
    },
    {
      label: "AI summary",
      value: detail.data.caption,
      divider: true
    },
    {
      label: "AI keywords",
      value: detail.data.keywords && detail.data.keywords.length > 0 ? detail.data.keywords.map((keyword) => ({
        label: keyword,
        color: "default"
      })) : void 0,
      divider: true
    }
  ];
  if (isResourceDetailAudio(detail)) return [
    {
      label: "Title",
      value: detail.data.attributes?.song?.title
    },
    {
      label: "Artist",
      value: detail.data.attributes?.song?.artist
    },
    {
      label: "Album",
      value: detail.data.attributes?.song?.album
    },
    {
      label: "Genre",
      value: detail.data.attributes?.song?.genre
    },
    {
      label: "Duration",
      value: formatter(detail.data.attributes?.audio?.duration, formatDuration)
    },
    {
      label: "Codec",
      value: detail.data.attributes?.audio?.codec
    },
    {
      label: "Audio Channels",
      value: detail.data.attributes?.audio?.channels
    },
    {
      label: "Sample Rate",
      value: formatter(detail.data.attributes?.audio?.sampleRate, (sampleRate) => `${sampleRate} Hz (${formatSampleRate(sampleRate)})`)
    },
    {
      label: "Bit Rate",
      value: formatter(detail.data.attributes?.audio?.bitRate, (bitRate) => `${parseIntOrString(bitRate).toLocaleString("en-US", {
        style: "decimal",
        maximumFractionDigits: 0,
        useGrouping: true
      })} bps (${formatBitrate(bitRate)})`)
    },
    {
      label: "AI summary",
      value: detail.data.caption,
      divider: true
    },
    {
      label: "AI keywords",
      value: detail.data.keywords && detail.data.keywords.length > 0 ? detail.data.keywords.map((keyword) => ({
        label: keyword,
        color: "default"
      })) : void 0,
      divider: true
    }
  ];
  return [];
};
const maxPlainTextLength = 80 * 1e4;
const maxDocumentPreviewSize = 5 * 1024 * 1024;

export { detailsToAttributeMaps, maxDocumentPreviewSize, maxPlainTextLength };
//# sourceMappingURL=resource-details.config.js.map
